import React from "react";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { formatDate, formatMoneyRub, VID_PLAT_META } from "./doc-plat-utils";

export const DEFAULT_VISIBLE_COLUMN_KEYS = [
  "idReg",
  "fdate",
  "kassaName",
  "vid",
  "predName",
  "symaExp",
  "komment",
  "txtOsn"
];

export const buildDefaultGridSettings = (columns) => {
  const keys = (columns || []).map((c) => c.key).filter(Boolean);
  const visible = new Set(DEFAULT_VISIBLE_COLUMN_KEYS);
  return {
    order: keys,
    hidden: keys.filter((k) => !visible.has(k))
  };
};

const sortIcon = ({ sortOrder }) => {
  if (sortOrder === "ascend") return <CaretUpOutlined />;
  if (sortOrder === "descend") return <CaretDownOutlined />;
  return null;
};

const vidRender = (vid, record) => {
  const meta = VID_PLAT_META[vid] || { label: `Вид ${vid}` };
  return (
    <span
      className={`doc-plats__badge${record.isDel ? " doc-plats__badge--deleted" : ""}`}
      data-vid={vid}
    >
      {meta.label}
    </span>
  );
};

/**
 * Колонки Delphi TVSpisok.
 * Ключи сортируемых колонок совпадают с sortBy API.
 * autoWidths — измеренные ширины для колонок без фиксированного размера.
 */
export const buildColumns = ({ sortBy, sortDir, autoWidths } = {}) => {
  const order = sortDir === "desc" ? "descend" : "ascend";
  const vidW = autoWidths?.vid ?? 140;
  const symaW = autoWidths?.symaExp ?? 120;
  const predW = autoWidths?.predName ?? 160;

  const col = (def, sortKey = def.key) => {
    const key = sortKey || def.key;
    if (!key) return def;
    const base = { ...def, key };
    if (!sortKey) return base;
    return {
      ...base,
      sorter: true,
      sortOrder: sortBy === key ? order : null,
      sortIcon,
      showSorterTooltip: false
    };
  };

  return [
    col(
      { key: "idReg", title: "#", dataIndex: "idReg", width: 80, className: "doc-plats__col-num" },
      "idReg"
    ),
    col(
      {
        key: "fdate",
        title: "Дата",
        dataIndex: "fdate",
        width: 100,
        className: "doc-plats__col-date",
        render: (v) => formatDate(v)
      },
      "fdate"
    ),
    col(
      {
        key: "kassaName",
        title: "Касса",
        dataIndex: "kassaName",
        width: 150,
        ellipsis: true,
        className: "doc-plats__col-muted"
      },
      "kassaName"
    ),
    col(
      {
        key: "vid",
        title: "Вид",
        dataIndex: "vid",
        width: vidW,
        className: "doc-plats__col-auto",
        render: vidRender
      },
      "vid"
    ),
    col(
      {
        key: "predName",
        title: "Контрагент",
        dataIndex: "predName",
        width: predW,
        ellipsis: true,
        className: "doc-plats__col-auto doc-plats__col-name"
      },
      "predName"
    ),
    col(
      {
        key: "symaExp",
        title: "Сумма",
        dataIndex: "symaExp",
        width: symaW,
        className: "doc-plats__col-auto doc-plats__col-total",
        render: (v) => formatMoneyRub(v)
      },
      "symaExp"
    ),
    col(
      {
        key: "komment",
        title: "Комментарий",
        dataIndex: "komment",
        width: 220,
        ellipsis: true,
        className: "doc-plats__col-muted"
      },
      "komment"
    ),
    col(
      {
        key: "txtOsn",
        title: "Назначение",
        dataIndex: "txtOsn",
        width: 140,
        ellipsis: true,
        className: "doc-plats__col-purpose"
      },
      "txtOsn"
    ),
    col(
      {
        key: "docnum",
        title: "№ документа",
        dataIndex: "docnum",
        width: 100,
        className: "doc-plats__col-num"
      },
      "docnum"
    ),
    col({ key: "qty", title: "Кол.", dataIndex: "qty", width: 70 }, "qty"),
    col(
      {
        key: "tovName",
        title: "Товар / услуга",
        dataIndex: "tovName",
        width: 160,
        ellipsis: true
      },
      "tovName"
    ),
    col({ key: "idOsn", title: "Осн.", dataIndex: "idOsn", width: 70 }, "idOsn"),
    col(
      {
        key: "isDel",
        title: "Удалено",
        dataIndex: "isDel",
        width: 80,
        render: (v) => (v ? "✓" : "")
      },
      "isDel"
    ),
    col(
      {
        key: "keyCardShort",
        title: "Карта",
        dataIndex: "keyCardShort",
        width: 90,
        ellipsis: true
      },
      "keyCardShort"
    ),
    col(
      {
        key: "dateUse",
        title: "Дата исп.",
        dataIndex: "dateUse",
        width: 100,
        render: (v) => formatDate(v)
      },
      "dateUse"
    ),
    col({ key: "vidOsn", title: "Вид основания", dataIndex: "vidOsn", width: 110 }, "vidOsn"),
    col(
      { key: "avtorName", title: "Автор", dataIndex: "avtorName", width: 120, ellipsis: true },
      "avtorName"
    ),
    col({ key: "idCheck", title: "Чек #", dataIndex: "idCheck", width: 80 }, "idCheck"),
    col(
      { key: "sklName", title: "Склад", dataIndex: "sklName", width: 120, ellipsis: true },
      "sklName"
    ),
    col({ key: "mark", title: "Маркировка", dataIndex: "mark", width: 120, ellipsis: true }, "mark"),
    col(
      {
        key: "hsReqId",
        title: "ЧЗ #",
        dataIndex: "hsReqId",
        width: 100,
        ellipsis: true
      },
      "hsReqId"
    ),
    col(
      {
        key: "hsReqTimestamp",
        title: "ЧЗ Дата время",
        dataIndex: "hsReqTimestamp",
        width: 140,
        ellipsis: true
      },
      "hsReqTimestamp"
    )
  ];
};
